// frontend/src/types/task.ts

// Task type union - all 8 task types
export type TaskType =
  | 'cleaning'
  | 'calibration'
  | 'replacement'
  | 'media_change'
  | 'water_change'
  | 'inspection'
  | 'dosing'
  | 'testing'
  | 'custom';

// Instance status union
export type InstanceStatus = 'pending' | 'completed' | 'skipped' | 'snoozed';

// Task definition status
export type TaskDefinitionStatus = 'active' | 'paused' | 'archived';

// Type-specific template data
export interface TaskTemplateData {
  // For dosing tasks
  chemical?: string;
  amount?: number;
  brand?: string;

  // For testing tasks
  parameter?: string;

  // For media change
  media_type?: string;

  // For water change
  volume?: number;
  percentage?: number;

  // General
  instructions?: string;
}

// Task definition (template for recurring tasks)
export interface TaskDefinition {
  id: string;
  tank_id: string;
  name: string;
  task_type: TaskType;
  recurrence?: Recurrence;
  equipment_id?: string;
  template_data: TaskTemplateData;
  status: TaskDefinitionStatus;
  created_at: string;
  updated_at: string;

  // Joined data
  equipment_name?: string;

  // Recurrence progress (populated when max_recurrences is set)
  completion_count?: number;

  // Earliest pending instance due date (from backend subquery)
  next_due_at?: string;
}

// Create task definition request
export interface TaskDefinitionCreate {
  name: string;
  task_type: TaskType;
  recurrence?: Recurrence | null;
  equipment_id?: string;
  template_data?: TaskTemplateData;
  // For one-time tasks: optional due date (defaults to now if not specified)
  due_date?: string;
  // Optional notes for one-time tasks
  notes?: string;
}

// Update task definition request
export interface TaskDefinitionUpdate {
  name?: string;
  task_type?: TaskType;
  recurrence?: Recurrence | null;
  equipment_id?: string;
  template_data?: TaskTemplateData;
  status?: TaskDefinitionStatus;
}

// Task instance (specific occurrence of a task)
export interface TaskInstance {
  id: string;
  definition_id: string;
  tank_id: string;
  due_at: string;
  status: InstanceStatus;
  snoozed_until?: string;
  created_at: string;
  updated_at: string;

  // Joined from definition
  name?: string;
  task_type?: TaskType;
  template_data?: TaskTemplateData;
  equipment_name?: string;
  equipment_id?: string;

  // Recurrence progress (populated when max_recurrences is set)
  recurrence_limit?: number;
  completion_count?: number;
}

// Task completion (historical record)
export interface TaskCompletion {
  id: string;
  instance_id?: string;
  definition_id: string;
  tank_id: string;
  completed_at: string;
  completion_data: Record<string, unknown>;
  notes?: string;
  linked_dosing_id?: number;
  linked_water_test_id?: number;
  equipment_id?: string;
  created_at: string;

  // Joined from definition
  name?: string;
  task_type?: TaskType;
  equipment_name?: string;
}

// Data for completing a task
export interface CompleteTaskData {
  completed_at?: string;
  notes?: string;

  // For dosing tasks
  chemical?: string;
  amount?: number;
  brand?: string;

  // For testing tasks
  parameter?: string;
  value?: number;

  // For water change tasks
  volume?: number;
  percentage?: number;

  // Equipment link
  equipment_id?: string;
}

// Snooze options
export interface SnoozeOptions {
  snooze_until: string; // ISO datetime
}

// Response types
export interface TaskDefinitionListResponse {
  definitions: TaskDefinition[];
  total: number;
}

export interface TaskInstanceListResponse {
  instances: TaskInstance[];
  total: number;
  overdue_count: number;
  due_today_count: number;
}

export interface TaskCompletionListResponse {
  completions: TaskCompletion[];
  total: number;
}

export interface TaskTypesResponse {
  types: TaskType[];
  labels: Record<TaskType, string>;
  data_generating_types: TaskType[];
}

// Display labels for task types
export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  cleaning: 'Cleaning',
  calibration: 'Calibration',
  replacement: 'Replacement',
  media_change: 'Media Change',
  water_change: 'Water Change',
  inspection: 'Inspection',
  dosing: 'Dosing',
  testing: 'Testing',
  custom: 'Custom',
};

// ============================================================================
// NEW RECURRENCE TYPES (unified recurrence model)
// ============================================================================

// Recurrence unit options
export type RecurrenceUnit = 'day' | 'week' | 'month';

export const RECURRENCE_UNIT_OPTIONS = [
  { value: 'day', label: 'day', labelPlural: 'days' },
  { value: 'week', label: 'week', labelPlural: 'weeks' },
  { value: 'month', label: 'month', labelPlural: 'months' },
] as const;

// Days of week for weekly recurrence
export const DAYS_OF_WEEK = [
  { value: 0, label: 'M', fullLabel: 'Monday' },
  { value: 1, label: 'T', fullLabel: 'Tuesday' },
  { value: 2, label: 'W', fullLabel: 'Wednesday' },
  { value: 3, label: 'T', fullLabel: 'Thursday' },
  { value: 4, label: 'F', fullLabel: 'Friday' },
  { value: 5, label: 'S', fullLabel: 'Saturday' },
  { value: 6, label: 'S', fullLabel: 'Sunday' },
] as const;

// Week of month options for monthly nth weekday
export const NEW_WEEK_OF_MONTH_OPTIONS = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: 5, label: 'Last' },
] as const;

// Monthly recurrence type
export type MonthlyType = 'day_of_month' | 'nth_weekday';

// Unified recurrence configuration
export interface Recurrence {
  interval: number;           // every N...
  unit: RecurrenceUnit;       // day | week | month
  days_of_week: number[];     // 0-6 for weekly (multi-select)
  day_of_month?: number;      // 1-31 for monthly
  week_of_month?: number;     // 1-5 for monthly (5=last)
  time_of_day?: string;       // HH:MM
  max_recurrences?: number;   // Auto-archive after N completions
}

// Helper to determine if a task has recurrence configured
export function isRecurring(definition: TaskDefinition): boolean {
  return definition.recurrence != null;
}

// Helper to determine if recurrence is rolling
export function isRollingRecurrence(r: Recurrence): boolean {
  if (r.unit === 'day') return true;
  if (r.unit === 'week' && r.days_of_week.length === 0) return true;
  if (r.unit === 'month' && r.day_of_month == null && r.week_of_month == null && r.days_of_week.length === 0) return true;
  return false;
}

// Day names for summary text
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Ordinal suffixes for day-of-month
const ORDINAL_SUFFIXES: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd', 21: 'st', 22: 'nd', 23: 'rd', 31: 'st' };
function ordinal(n: number): string {
  return `${n}${ORDINAL_SUFFIXES[n] || 'th'}`;
}

// Format HH:MM to 12-hour with AM/PM
function formatTime12h(time: string): string {
  const parts = time.split(':').map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
}

export interface RecurrenceSummary {
  headline: string;
  detail: string;
}

/**
 * Build a human-readable summary of a recurrence configuration.
 * Used for the preview banner in the task creation form.
 */
export function buildRecurrenceSummary(r: Recurrence, taskName: string): RecurrenceSummary {
  const name = taskName.trim() || 'This task';
  const rolling = isRollingRecurrence(r);
  const unitLabel = r.interval === 1
    ? (r.unit === 'day' ? 'day' : r.unit === 'week' ? 'week' : 'month')
    : (r.unit === 'day' ? 'days' : r.unit === 'week' ? 'weeks' : 'months');
  const intervalStr = r.interval === 1 ? unitLabel : `${r.interval} ${unitLabel}`;
  const timeSuffix = r.time_of_day ? ` at ${formatTime12h(r.time_of_day)}` : '';
  const limitSuffix = r.max_recurrences ? `, ${r.max_recurrences} times total` : '';
  const limitDetail = r.max_recurrences
    ? ` Task will auto-complete after ${r.max_recurrences} times.`
    : '';

  if (rolling) {
    return {
      headline: `${name} repeats every ${intervalStr} after completion${limitSuffix}`,
      detail: `The countdown resets each time you mark it done.${limitDetail}`,
    };
  }

  // Fixed weekly
  if (r.unit === 'week' && r.days_of_week.length > 0) {
    const dayNames = r.days_of_week.map((d) => DAY_NAMES[d]).join(', ');
    return {
      headline: `${name} will be due every ${intervalStr} on ${dayNames}${timeSuffix}${limitSuffix}`,
      detail: `Stays on schedule even if you complete one late.${limitDetail}`,
    };
  }

  // Fixed monthly - nth weekday
  if (r.unit === 'month' && r.week_of_month != null && r.days_of_week.length > 0) {
    const weekLabel = NEW_WEEK_OF_MONTH_OPTIONS.find(w => w.value === r.week_of_month)?.label ?? '1st';
    const dayName = DAY_NAMES[r.days_of_week[0]!];
    const monthInterval = r.interval === 1 ? 'every month' : `every ${r.interval} months`;
    return {
      headline: `${name} will be due on the ${weekLabel} ${dayName} of ${monthInterval}${timeSuffix}${limitSuffix}`,
      detail: `Stays on schedule even if you complete one late.${limitDetail}`,
    };
  }

  // Fixed monthly - day of month
  if (r.unit === 'month' && r.day_of_month != null) {
    const dayStr = ordinal(r.day_of_month);
    if (r.interval === 1) {
      return {
        headline: `${name} will be due on the ${dayStr} of every month${timeSuffix}${limitSuffix}`,
        detail: `Stays on schedule even if you complete one late.${limitDetail}`,
      };
    }
    return {
      headline: `${name} will be due on the ${dayStr} every ${r.interval} months${timeSuffix}${limitSuffix}`,
      detail: `Stays on schedule even if you complete one late.${limitDetail}`,
    };
  }

  // Fallback for monthly without specific day (rolling behavior)
  return {
    headline: `${name} repeats every ${intervalStr} after completion${limitSuffix}`,
    detail: `The countdown resets each time you mark it done.${limitDetail}`,
  };
}

/**
 * Build a short schedule label for display on task cards.
 * Returns null for one-time tasks (no recurrence).
 * Examples: "Every 3 days", "Every Friday", "Monthly on the 15th"
 */
export function getScheduleLabel(r: Recurrence | null | undefined): string | null {
  if (!r) return null;

  const rolling = isRollingRecurrence(r);
  const limitSuffix = r.max_recurrences ? ` (${r.max_recurrences}x)` : '';

  let label: string;

  if (rolling) {
    if (r.unit === 'day') {
      label = r.interval === 1 ? 'Daily' : `Every ${r.interval} days`;
    } else if (r.unit === 'week') {
      label = r.interval === 1 ? 'Weekly' : `Every ${r.interval} weeks`;
    } else {
      label = r.interval === 1 ? 'Monthly' : `Every ${r.interval} months`;
    }
    return label + limitSuffix;
  }

  // Fixed weekly
  if (r.unit === 'week' && r.days_of_week.length > 0) {
    const dayNames = r.days_of_week.map((d) => DAY_NAMES[d]);
    if (r.interval === 1) {
      label = dayNames.length === 1 ? `Every ${dayNames[0]}` : `Weekly: ${dayNames.join(', ')}`;
    } else {
      label = `Every ${r.interval} weeks: ${dayNames.join(', ')}`;
    }
    return label + limitSuffix;
  }

  // Fixed monthly - nth weekday
  if (r.unit === 'month' && r.week_of_month != null && r.days_of_week.length > 0) {
    const weekLabel = NEW_WEEK_OF_MONTH_OPTIONS.find(w => w.value === r.week_of_month)?.label ?? '1st';
    const dayName = DAY_NAMES[r.days_of_week[0]!];
    return `Monthly: ${weekLabel} ${dayName}${limitSuffix}`;
  }

  // Fixed monthly - day of month
  if (r.unit === 'month' && r.day_of_month != null) {
    return `Monthly on the ${ordinal(r.day_of_month)}${limitSuffix}`;
  }

  label = r.interval === 1 ? 'Recurring' : `Every ${r.interval} ${r.unit}s`;
  return label + limitSuffix;
}

// Short day abbreviations for compact labels
const DAY_ABBREVS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * Build a compact schedule label for the row-based task view.
 * Returns null for one-time tasks (no recurrence).
 * Examples: "1d", "2w", "MoFr", "1mo"
 */
export function getCompactScheduleLabel(r: Recurrence | null | undefined): string | null {
  if (!r) return null;

  const unitMap: Record<RecurrenceUnit, string> = { day: 'd', week: 'w', month: 'mo' };

  // Fixed weekly with specific days — show day abbreviations
  if (r.unit === 'week' && r.days_of_week.length > 0) {
    const days = r.days_of_week.map((d) => DAY_ABBREVS[d]).join('');
    return r.interval > 1 ? `${r.interval}w ${days}` : days;
  }

  if (r.interval === 1) {
    if (r.unit === 'day') return '1d';
    if (r.unit === 'week') return '1w';
    if (r.unit === 'month') return '1mo';
  }

  return `${r.interval}${unitMap[r.unit]}`;
}

// ============================================================================
// END NEW RECURRENCE TYPES
// ============================================================================

// Colors for task types (for UI badges/icons)
export const TASK_TYPE_COLORS: Record<TaskType, string> = {
  cleaning: 'teal',
  calibration: 'ocean',
  replacement: 'coral',
  media_change: 'teal',
  water_change: 'ocean',
  inspection: 'ocean',
  dosing: 'coral',
  testing: 'teal',
  custom: 'gray',
};

// Task category groupings for All Tasks sub-tabs
export type TaskCategory = 'water_management' | 'maintenance' | 'custom';

export const TASK_CATEGORY_LABELS: Record<TaskCategory, string> = {
  water_management: 'Water Management',
  maintenance: 'Maintenance',
  custom: 'Custom',
};

export const TASK_CATEGORY_MAP: Record<TaskType, TaskCategory> = {
  water_change: 'water_management',
  dosing: 'water_management',
  testing: 'water_management',
  cleaning: 'maintenance',
  calibration: 'maintenance',
  replacement: 'maintenance',
  media_change: 'maintenance',
  inspection: 'maintenance',
  custom: 'custom',
};

// Get types belonging to a category
export function getTaskTypesForCategory(category: TaskCategory): TaskType[] {
  return Object.entries(TASK_CATEGORY_MAP)
    .filter(([, cat]) => cat === category)
    .map(([type]) => type as TaskType);
}

// Display labels for instance statuses
export const INSTANCE_STATUS_LABELS: Record<InstanceStatus, string> = {
  pending: 'Pending',
  completed: 'Completed',
  skipped: 'Skipped',
  snoozed: 'Snoozed',
};

// Data-generating task types (create linked records)
export const DATA_GENERATING_TYPES: TaskType[] = ['dosing', 'testing'];
